import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useRole, UserRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { useAppData } from "@/data/AppDataContext";
import { useUISkin } from "@/data/UISkinContext";
import { useOrderDraft } from "@/data/OrderDraftContext";
import {
  CalendarDays, Clock, AlertTriangle, DollarSign,
  ArrowRight, TrendingUp, ClipboardCheck, Send,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ContextMetric {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  link?: string;
}

interface QuickAction {
  label: string;
  path: string;
  icon: React.ElementType;
  variant: "default" | "warning" | "success";
}

function getRoleQuickActions(role: UserRole): QuickAction[] {
  switch (role) {
    case "sales":
      return [
        { label: "Start Appointment", path: "/appointments", icon: CalendarDays, variant: "success" },
        { label: "View Follow-Ups", path: "/follow-ups", icon: Clock, variant: "default" },
        { label: "At-Risk Deals", path: "/opportunities", icon: AlertTriangle, variant: "warning" },
      ];
    case "sales_manager":
      return [
        { label: "Rep Scorecards", path: "/rep-scorecards", icon: ClipboardCheck, variant: "success" },
        { label: "Appointment Results", path: "/appointment-results", icon: CalendarDays, variant: "default" },
        { label: "Commissions", path: "/commissions", icon: DollarSign, variant: "default" },
      ];
    case "call_center":
      return [
        { label: "View Leads", path: "/pipeline", icon: TrendingUp, variant: "success" },
        { label: "Conversations", path: "/messages", icon: Clock, variant: "default" },
        { label: "Follow-Ups", path: "/follow-ups", icon: Clock, variant: "default" },
      ];
    case "owner":
    case "super_admin":
    case "admin":
    case "it_staff":
      return [
        { label: "Command Dashboard", path: "/command-dashboard", icon: TrendingUp, variant: "success" },
        { label: "View Follow-Ups", path: "/follow-ups", icon: Clock, variant: "default" },
        { label: "Rep Scorecards", path: "/rep-scorecards", icon: ClipboardCheck, variant: "default" },
      ];
    case "ops_manager":
      return [
        { label: "Dispatch Board", path: "/scheduling-dispatch", icon: CalendarDays, variant: "success" },
        { label: "Install Readiness", path: "/install-readiness", icon: ClipboardCheck, variant: "default" },
        { label: "At-Risk Jobs", path: "/jobs", icon: AlertTriangle, variant: "warning" },
      ];
    case "cfo":
    case "finance_manager":
      return [
        { label: "Commissions", path: "/commissions", icon: DollarSign, variant: "success" },
        { label: "Revenue Intel", path: "/revenue-intelligence", icon: TrendingUp, variant: "default" },
      ];
    default:
      return [];
  }
}

const ACTION_STYLES: Record<string, string> = {
  default: "bg-muted hover:bg-muted/80 text-foreground",
  warning: "bg-warning/10 hover:bg-warning/20 text-warning border-warning/20",
  success: "bg-primary/10 hover:bg-primary/20 text-primary border-primary/20",
};

export function SmartContextBanner() {
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { jobs, issues, followUpTasks, appointmentResults } = useAppData();
  const { drafts } = useOrderDraft();

  const metrics = useMemo<ContextMetric[]>(() => {
    const now = new Date();
    const overdueFollowUps = (followUpTasks || []).filter(
      (t) => t.status === "pending" && new Date(t.dueDate) < now
    ).length;

    const activeStages: string[] = ["Completed", "Issue"];
    const atRiskJobs = jobs.filter(
      (j) => !activeStages.includes(j.currentStage) && j.issueFlag
    ).length;

    const revenueAtRisk = jobs
      .filter((j) => !activeStages.includes(j.currentStage) && j.issueFlag)
      .reduce((s, j) => s + (j.financials?.contractValue || 0), 0);

    const todayResults = (appointmentResults || []).filter(
      (r) => new Date(r.submittedAt).toDateString() === now.toDateString()
    ).length;

    const baseMetrics: ContextMetric[] = [];

    if (hasPerm("leads_view")) {
      if (overdueFollowUps > 0) {
        baseMetrics.push({
          label: overdueFollowUps === 1 ? "Deal at Risk — Overdue" : "Deals at Risk — Overdue",
          value: overdueFollowUps,
          icon: Clock,
          color: "text-warning",
          link: "/follow-ups",
        });
      }
    }

    if (hasPerm("jobs_view")) {
      if (atRiskJobs > 0) {
        baseMetrics.push({
          label: atRiskJobs === 1 ? "Job Delayed" : "Jobs Delayed",
          value: atRiskJobs,
          icon: AlertTriangle,
          color: "text-destructive",
          link: "/jobs",
        });
      }
      if (revenueAtRisk > 0) {
        baseMetrics.push({
          label: "Revenue at Risk",
          value: `$${(revenueAtRisk / 1000).toFixed(0)}k`,
          icon: DollarSign,
          color: "text-destructive",
          link: "/command-dashboard",
        });
      }
    }

    if (["sales", "sales_manager", "owner", "super_admin"].includes(role) && todayResults > 0) {
      baseMetrics.push({
        label: todayResults === 1 ? "Result Today" : "Results Today",
        value: todayResults,
        icon: CalendarDays,
        color: "text-primary",
        link: "/appointment-results",
      });
    }

    // Orders prepared awaiting push — visible to ordering/ops/leadership roles.
    if (hasPerm("ordering_view")) {
      const preparedCount = Object.values(drafts).filter((d) => d.status === "prepared").length;
      if (preparedCount > 0) {
        baseMetrics.push({
          label: preparedCount === 1 ? "Order Prepared — Awaiting Push" : "Orders Prepared — Awaiting Push",
          value: preparedCount,
          icon: Send,
          color: "text-info",
          link: "/ordering-queue",
        });
      }
    }

    return baseMetrics;
  }, [role, jobs, issues, followUpTasks, appointmentResults, drafts]);

  const quickActions = getRoleQuickActions(role);
  const { skin } = useUISkin();

  // Cockpit skin: banner intentionally suppressed — the page-level intelligence
  // (What Needs Attention, Live Actions, Revenue Leak) already carries this signal.
  if (skin === "cockpit") return null;

  // Don't render for roles with no contextual data
  if (metrics.length === 0 && quickActions.length === 0) return null;

  /* Cockpit skin returns null above — only classic renders below. */
  return (
    <div className="bg-card border-b px-4 md:px-6 py-2 flex items-center gap-4 overflow-x-auto">
      {/* Metrics */}
      {metrics.length > 0 && (
        <div className="flex items-center gap-3 shrink-0">
          {metrics.map((m) => {
            const Icon = m.icon;
            const content = (
              <div key={m.label} className="flex items-center gap-1.5">
                <Icon className={cn("h-3.5 w-3.5", m.color)} />
                <span className={cn("text-xs font-bold", m.color)}>{m.value}</span>
                <span className="text-[10px] text-muted-foreground">{m.label}</span>
              </div>
            );
            return m.link ? (
              <Link key={m.label} to={m.link} className="hover:opacity-80 transition-opacity">
                {content}
              </Link>
            ) : (
              content
            );
          })}
        </div>
      )}

      {/* Separator */}
      {metrics.length > 0 && quickActions.length > 0 && (
        <div className="h-5 w-px bg-border shrink-0" />
      )}

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {quickActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.label}
                to={a.path}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors",
                  ACTION_STYLES[a.variant]
                )}
              >
                <Icon className="h-3 w-3" />
                {a.label}
                <ArrowRight className="h-2.5 w-2.5 opacity-50" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
